import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldAlert, FileText, Download } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Invisible to humans, irresistible to bots. Must stay empty — the server rejects submissions that fill it.
export const Honeypot = ({ value, onChange, testid }) => (
  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
    <label>
      Website
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  </div>
);

// Server-issued human verification (math question). Fetch on demand; one-time use.
export const useChallenge = () => {
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/security/challenge`);
      setChallenge(data);
      setAnswer("");
    } catch {
      toast.error("Could not load the verification question — try again.");
    }
  }, []);
  return {
    challenge,
    answer,
    setAnswer,
    refresh,
    clear: () => { setChallenge(null); setAnswer(""); },
    payload: () => (challenge ? { challenge_id: challenge.id, challenge_answer: answer } : {}),
  };
};

export const ChallengeField = ({ challenge, answer, setAnswer, onRefresh, testid }) => {
  if (!challenge) return null;
  return (
    <div className="border border-clinical/30 bg-[#0B101C] rounded-xl px-4 py-3.5 flex items-center gap-3 flex-wrap" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-clinical">Human check</p>
      <p className="font-mono text-sm text-ink" data-testid={`${testid}-question`}>{challenge.question}</p>
      <input
        type="text"
        inputMode="numeric"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        data-testid={`${testid}-answer`}
        className="w-20 rounded-lg border border-[#232B40] bg-[#0B101C] text-ink text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-clinical"
        placeholder="?"
      />
      <button type="button" onClick={onRefresh} data-testid={`${testid}-refresh`} className="text-[#8E96AB] hover:text-clinical transition-colors duration-300" aria-label="New question">
        <RefreshCw className="h-4 w-4" />
      </button>
    </div>
  );
};

// Structured security error from the server ({code, message|reason, review, ...}) or null
export const securityDetail = (err) => {
  const d = err?.response?.data?.detail;
  return d && typeof d === "object" ? d : null;
};

const ACTION_LABELS = {
  restrict: "temporarily restricted",
  suspend: "suspended",
  ban: "banned",
  "ip-block": "restricted",
};

// Clear explanation + admin-review path when a security rule stops a legitimate user
export const ModeratedNotice = ({ detail, testid }) => {
  const { token } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const requestReview = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/security/request-review`, { message: "" }, { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
      toast.success(`Review requested — ticket ${data.ticket_number}. You'll hear back in Mission Support.`);
    } catch {
      toast.error("Could not send the review request.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="border border-[#C0392B]/40 bg-paper rounded-2xl p-6 space-y-3" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.25em] text-[#C0392B] flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" strokeWidth={1.5} /> VetForge Security
      </p>
      <p className="text-sm text-[#B9BFCE] leading-relaxed">
        {detail.code === "moderated"
          ? <>This account is {ACTION_LABELS[detail.action] || "restricted"}{detail.expires_at ? <> until <b className="text-ink">{new Date(detail.expires_at).toLocaleString()}</b></> : ""}. Reason: {detail.reason || "security policy"}.</>
          : detail.message || "This action was blocked by VetForge security."}
      </p>
      {detail.review && !sent && (
        <button onClick={requestReview} disabled={sending} data-testid={`${testid}-review`} className="inline-flex items-center gap-2 border border-clinical/40 text-clinical text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-lg hover:bg-clinical hover:text-[#0B101C] transition-colors duration-300 disabled:opacity-50">
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Request an admin review
        </button>
      )}
      {sent && <p className="text-[10px] uppercase tracking-[0.2em] text-clinical" data-testid={`${testid}-reviewed`}>Review request sent — watch Mission Support</p>}
    </div>
  );
};

// Clickable links in post bodies
export const LinkifiedText = ({ text, className = "" }) => {
  const parts = (text || "").split(/(https?:\/\/[^\s]+|www\.[^\s]+)/g);
  return (
    <p className={className} style={{ whiteSpace: "pre-line" }}>
      {parts.map((part, i) =>
        /^(https?:\/\/|www\.)/.test(part) ? (
          <a key={i} href={part.startsWith("http") ? part : `https://${part}`} target="_blank" rel="noreferrer nofollow" className="text-clinical underline underline-offset-4 hover:text-ink break-all transition-colors duration-300">
            {part}
          </a>
        ) : (
          part
        )
      )}
    </p>
  );
};

export const uploadDiscussionFile = async (file, headers) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/discussions/uploads`, form, { headers });
  return data; // {id, name, content_type, size}
};

// Renders uploaded attachments; files are members-only so they load with the auth header
const AttachmentItem = ({ att, headers }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    axios.get(`${API}/discussions/files/${att.id}`, { headers, responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.id]);
  if ((att.content_type || "").startsWith("image/")) {
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`attachment-${att.id.slice(0, 8)}`}>
        <img src={url} alt={att.name} className="max-h-56 rounded-xl border border-[#232B40] object-contain bg-[#0B101C]" />
      </a>
    ) : (
      <p className="text-[10px] text-[#8E96AB] uppercase tracking-[0.2em] inline-flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading image</p>
    );
  }
  return url ? (
    <a href={url} download={att.name} data-testid={`attachment-${att.id.slice(0, 8)}`} className="inline-flex items-center gap-2 border border-[#232B40] rounded-lg px-3 py-2 text-xs text-clinical hover:bg-[#1A233A] transition-colors duration-300">
      <FileText className="h-3.5 w-3.5" /> {att.name} <Download className="h-3 w-3" />
    </a>
  ) : null;
};

export const AttachmentList = ({ items, headers }) => {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-3 pt-2" data-testid="attachment-list">
      {items.map((a) => <AttachmentItem key={a.id} att={a} headers={headers} />)}
    </div>
  );
};

// Shared file-picker state for post/reply forms (up to 3 files)
export const useAttachments = (headers) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const upload = async () => {
    if (!files.length) return [];
    setUploading(true);
    try {
      const out = [];
      for (const f of files.slice(0, 3)) out.push(await uploadDiscussionFile(f, headers));
      setFiles([]);
      return out;
    } catch (err) {
      toast.error(err.response?.data?.detail || "File upload failed.");
      throw err;
    } finally {
      setUploading(false);
    }
  };
  return { files, setFiles, upload, uploading };
};
